/**
 * Tema Material-UI alineado con el Brand & Style Guide de QUANTIA.
 *
 * Filosofía: modo claro (background lavanda, tarjetas blancas) con
 * acentos oscuros premium (header, hero) que se aplican vía componentes
 * Quantia custom. Los tokens reales viven en `tokens.css` y este archivo
 * los refleja en JS para que MUI pueda consumirlos.
 */

import { createTheme } from '@mui/material/styles';

// Espejo en JS de los tokens definidos en tokens.css (algunos componentes
// que reciben colores via prop o sx necesitan acceso JS).
export const COLORS = {
  // Marca
  primary:        '#5B4FF0',
  primaryDark:    '#3B30C0',
  primaryDeeper:  '#251D9A',
  primaryLight:   '#EAE8FD',
  primaryMid:     '#9B91F5',

  accent:         '#C2E53A',
  accentDark:     '#9AB82A',
  accentDarker:   '#7AB820',
  accentLight:    '#D9EE79',
  accentPale:     '#EEF6CC',

  black:          '#0D0D0D',
  blackSoft:      '#1A1A1A',
  blackMuted:     '#2E2E2E',
  grayDark:       '#444444',
  gray:           '#888888',
  grayLight:      '#C8C8C8',
  graySoft:       '#F4F4F2',
  white:          '#FFFFFF',

  // Fondos de app
  background:     '#F6F5FF',  // lavanda — fondo principal
  backgroundLight:'#F4F4F2',  // gris suave — bloques internos
  backgroundPaper:'#FFFFFF',  // tarjetas/Paper

  // Texto
  textPrimary:    '#1A1A1A',
  textSecondary:  '#444444',
  textMuted:      '#888888',

  // Estados
  success:        '#22C55E',
  error:          '#EF4444',
  warning:        '#F59E0B',
  info:           '#3B82F6',
};

const FONT_DISPLAY = "'Fira Sans', sans-serif";
const FONT_BODY    = "'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: COLORS.primary,
      dark: COLORS.primaryDark,
      light: COLORS.primaryLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: COLORS.accent,
      dark: COLORS.accentDark,
      light: COLORS.accentLight,
      contrastText: COLORS.black,
    },
    background: {
      default: COLORS.background,
      paper: COLORS.backgroundPaper,
    },
    text: {
      primary: COLORS.textPrimary,
      secondary: COLORS.textSecondary,
      disabled: COLORS.gray,
    },
    success: { main: COLORS.success, contrastText: '#FFFFFF' },
    error:   { main: COLORS.error,   contrastText: '#FFFFFF' },
    warning: { main: COLORS.warning, contrastText: '#FFFFFF' },
    info:    { main: COLORS.info,    contrastText: '#FFFFFF' },
    divider: 'rgba(91, 79, 240, 0.12)',
  },

  shape: {
    borderRadius: 12,
  },

  typography: {
    fontFamily: FONT_BODY,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 900,
      fontSize: '3rem',          // 48px
      letterSpacing: '-0.03em',
      lineHeight: 1.05,
      color: COLORS.black,
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 900,
      fontSize: '2.25rem',       // 36px
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      color: COLORS.black,
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: '1.75rem',       // 28px
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      color: COLORS.black,
    },
    h4: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.375rem',      // 22px
      letterSpacing: '-0.01em',
      color: COLORS.black,
    },
    h5: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.125rem',      // 18px
      color: COLORS.black,
    },
    h6: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1rem',
      color: COLORS.black,
    },
    subtitle1: {
      fontFamily: FONT_BODY,
      fontWeight: 300,
      fontSize: '1.125rem',
      lineHeight: 1.65,
      color: COLORS.textSecondary,
    },
    subtitle2: {
      fontFamily: FONT_BODY,
      fontWeight: 600,
      fontSize: '0.875rem',
      color: COLORS.textSecondary,
    },
    body1: {
      fontFamily: FONT_BODY,
      fontSize: '1rem',
      lineHeight: 1.7,
      color: COLORS.textPrimary,
    },
    body2: {
      fontFamily: FONT_BODY,
      fontSize: '0.875rem',
      lineHeight: 1.65,
      color: COLORS.textSecondary,
    },
    caption: {
      fontFamily: FONT_BODY,
      fontSize: '0.75rem',
      color: COLORS.gray,
    },
    overline: {
      fontFamily: FONT_BODY,
      fontWeight: 600,
      fontSize: '0.6875rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: COLORS.gray,
    },
    button: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: COLORS.background,
          color: COLORS.textPrimary,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          fontFamily: FONT_DISPLAY,
          fontWeight: 700,
          textTransform: 'none',
          transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.98)',
          },
        },
        sizeSmall: {
          padding: '7px 16px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
          borderRadius: 16,
        },
        containedPrimary: {
          backgroundColor: COLORS.primary,
          color: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(91, 79, 240, 0.25)',
          '&:hover': {
            backgroundColor: COLORS.primaryDark,
            boxShadow: '0 8px 32px rgba(91, 79, 240, 0.35)',
          },
        },
        containedSecondary: {
          backgroundColor: COLORS.accent,
          color: COLORS.black,
          boxShadow: '0 4px 16px rgba(194, 229, 58, 0.30)',
          '&:hover': {
            backgroundColor: COLORS.accentDark,
            boxShadow: '0 4px 16px rgba(194, 229, 58, 0.45)',
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          borderColor: COLORS.primary,
          color: COLORS.primary,
          '&:hover': {
            backgroundColor: COLORS.primaryLight,
            borderWidth: 2,
            borderColor: COLORS.primary,
          },
        },
        text: {
          color: COLORS.primary,
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 24,
          backgroundColor: COLORS.white,
          border: '1px solid rgba(91, 79, 240, 0.08)',
          transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1), border-color 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'rgba(91, 79, 240, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: COLORS.white,
          borderRadius: 16,
          border: '1px solid rgba(91, 79, 240, 0.08)',
        },
        elevation0: { boxShadow: 'none' },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: COLORS.black,
          color: '#FFFFFF',
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 72,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: COLORS.white,
            transition: 'border-color 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': {
              borderColor: 'rgba(91, 79, 240, 0.2)',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(91, 79, 240, 0.45)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(91, 79, 240, 0.12)',
              '& fieldset': {
                borderColor: COLORS.primary,
                borderWidth: '1.5px',
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: COLORS.textSecondary,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: COLORS.primary,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        outlined: {
          borderRadius: 12,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 10,
          borderRadius: 6,
          backgroundColor: COLORS.primaryLight,
        },
        bar: {
          backgroundColor: COLORS.primary,
          borderRadius: 6,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_BODY,
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          borderRadius: 9999,
          height: 24,
        },
        colorPrimary: {
          backgroundColor: COLORS.primaryLight,
          color: COLORS.primaryDark,
        },
        colorSecondary: {
          backgroundColor: COLORS.accentPale,
          color: '#3a5a00',
        },
        colorSuccess: {
          backgroundColor: '#DCFCE7',
          color: '#166534',
        },
        colorWarning: {
          backgroundColor: '#FEF9C3',
          color: '#854D0E',
        },
        colorError: {
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
        },
        colorInfo: {
          backgroundColor: '#DBEAFE',
          color: '#1E40AF',
        },
        outlined: {
          borderColor: COLORS.primary,
          color: COLORS.primary,
          backgroundColor: 'transparent',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid transparent',
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: '#1D4ED8',
          borderColor: '#BFDBFE',
        },
        standardSuccess: {
          backgroundColor: '#F0FDF4',
          color: '#15803D',
          borderColor: '#BBF7D0',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#B45309',
          borderColor: '#FDE68A',
        },
        standardError: {
          backgroundColor: '#FFF1F2',
          color: '#BE123C',
          borderColor: '#FECDD3',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          backgroundColor: COLORS.primary,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontWeight: 700,
          fontSize: '0.875rem',
          textTransform: 'none',
          minHeight: 44,
          color: COLORS.textSecondary,
          '&.Mui-selected': {
            color: COLORS.primary,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(91, 79, 240, 0.1)',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: COLORS.graySoft,
            color: COLORS.black,
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderBottom: '1px solid rgba(91, 79, 240, 0.12)',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(91, 79, 240, 0.06)',
          fontFamily: FONT_BODY,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 180ms ease',
          '&:hover': {
            backgroundColor: 'rgba(91, 79, 240, 0.03)',
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color 180ms ease, transform 180ms ease',
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export default theme;
